/**
 * One answer, for the whole loyalty bundle, to "who is the fan" — read off `window.urcAuth`.
 *
 * This is what `authSignal.js` was: the single place the question is asked, so that adding a
 * consumer does not add a copy. It answers a strictly better question. `authSignal.js` read the
 * `auth0_authenticated` cookie — unsigned, forgeable, written by four different places and deleted
 * on every page load by a third-party script — and reported a sign-in but, by its own docblock,
 * never a sign-out. This reads a state derived from an ID token the SDK has already validated, and
 * reports sign-out and account switches as well.
 *
 * WHY A GLOBAL AND NOT AN IMPORT
 * ------------------------------
 * `window.urcAuth` is published by a separate Vite entry (`resources/scripts/auth/index.js`) and
 * nothing here may import from it. That is [D-055](../../../../docs/decisions.md): the entries
 * share one Rollup build, so an import would hoist a shared chunk and rewrite the module graph of
 * the already-shipped loyalty bundle — the bundle that carries the fan's credential. A global
 * creates none. `resources/scripts/tracking/` needs the same accessor and, for the same reason,
 * carries its own copy rather than importing this one.
 *
 * THREE PROPERTIES OF THE CONTRACT THAT EVERY CALLER HERE HAS TO KNOW
 * ------------------------------------------------------------------
 * 1. **`state()` answers `null` before the SDK has settled, and `null` is not "signed out."** On a
 *    cold page that is the first few hundred milliseconds. Anything that reveals must go through
 *    `whenSignedIn()`, `subscribeAuth()` or `authReady()`.
 * 2. **The global can be absent entirely**, not merely unsettled: `App\Auth\Assets::enqueue()`
 *    ships no bundle on an install with no `AUTH0_DOMAIN`. Every reader below degrades to "nobody
 *    is signed in", which is what such an install means, rather than throwing on every page.
 * 3. **`subscribe()` reports sign-out.** That is new, and it is why `useSignedIn` is no longer a
 *    one-way signal.
 *
 * Everything here takes its world as an argument through `createUrcAuthReader()`, so
 * `urcAuth.test.js` drives the whole thing under `node --test` where there is no `window`. That is
 * the split the rest of this folder follows — `./consent/store.js` and `./consent/machine.js` are
 * the other two — and it is the reason `authSignal.js`, which reached for `document` and `window`
 * directly, never had a test at all.
 */

/** What every reader degrades to when the contract is absent. Mirrors `auth/state.js`'s NO_FAN. */
const NOBODY = Object.freeze( {
	signedIn: false,
	sub: null,
	email: null,
	clubId: null,
} );

/**
 * Build a reader over an injected `window`-like object.
 *
 * @param {() => (Object|undefined)} getContract Returns the `urcAuth` object, or undefined.
 * @return {Object} The reader.
 */
export function createUrcAuthReader( getContract ) {
	/**
	 * The contract, or null when this install has no auth bundle.
	 *
	 * Re-read on every call rather than captured once. The bundle is a deferred module and this one
	 * may be evaluated in the same tick, so a value captured at import could be `undefined`
	 * forever — `App\Auth\Assets` enqueues at priority 9 to make the ordering hold, but a reader
	 * that depends on an enqueue priority is a reader that breaks when somebody changes one.
	 *
	 * @return {Object|null} The contract.
	 */
	function contract() {
		const value = getContract();

		return value && typeof value === 'object' ? value : null;
	}

	/**
	 * The fan right now, or null before the SDK has answered.
	 *
	 * A live, side-effect-free read, so it is safe during render — which is what `useSignedIn`'s
	 * lazy `useState` seed needs. An absent contract answers NOBODY rather than null: there is no
	 * SDK to wait for, so "not answered yet" would be a lie that never resolves.
	 *
	 * @return {Object|null} The AuthState, or null.
	 */
	function authState() {
		const api = contract();

		if ( ! api ) {
			return { ...NOBODY };
		}

		return typeof api.state === 'function' ? api.state() : null;
	}

	/**
	 * Whether the fan is known to be signed in *right now*.
	 *
	 * **False covers two different things** — "signed out" and "the SDK has not answered" — and
	 * that is deliberate for the callers that want a synchronous answer they can render. Anything
	 * that must tell them apart reads `authState()` and checks for `null`.
	 *
	 * @return {boolean} True only when a fan is established.
	 */
	function isSignedIn() {
		const state = authState();

		return Boolean( state && state.signedIn );
	}

	/**
	 * Every change: sign-in, sign-out and account switch. Fires immediately when already settled.
	 *
	 * @param {(state: Object) => void} listener Called with the AuthState.
	 * @return {() => void} Unsubscribe.
	 */
	function subscribeAuth( listener ) {
		const api = contract();

		if ( ! api || typeof api.subscribe !== 'function' ) {
			listener( { ...NOBODY } );

			return () => {};
		}

		return api.subscribe( listener );
	}

	/**
	 * Call `listener` once, as soon as a fan is established — synchronously if one already is.
	 *
	 * **Reveal-only, and that is a narrower contract than `subscribeAuth()` on purpose.** It never
	 * reports a sign-out, so a caller that only ever uncovers something (the home page's join
	 * strip) cannot accidentally acquire a collapse path it has no markup for. `authSignal.js`'s
	 * `whenSignedIn()` promised exactly this and could not do better; here it is a restriction
	 * rather than a limitation.
	 *
	 * @param {() => void} listener Called at most once.
	 * @return {() => void} Unsubscribe. Safe to call after the listener has fired.
	 */
	function whenSignedIn( listener ) {
		let fired = false;
		let stop = () => {};

		// The subscription can fire synchronously, before `stop` has been assigned -- so `fired`
		// is what unsubscribes in that case rather than an assumption about ordering.
		stop = subscribeAuth( state => {
			if ( fired || ! state || ! state.signedIn ) {
				return;
			}

			fired = true;
			stop();
			listener();
		} );

		if ( fired ) {
			stop();
		}

		return () => {
			fired = true;
			stop();
		};
	}

	/**
	 * Only when the fan changes — one fan replacing another, with or without a sign-out between.
	 *
	 * This is the reset signal. See ../loyalty.jsx.
	 *
	 * @param {(state: Object) => void} listener Called with the new AuthState.
	 * @return {() => void} Unsubscribe.
	 */
	function onIdentityChange( listener ) {
		const api = contract();

		if ( ! api || typeof api.onIdentityChange !== 'function' ) {
			return () => {};
		}

		return api.onIdentityChange( listener );
	}

	/**
	 * The AuthState, once the SDK has settled. Never rejects.
	 *
	 * @return {Promise<Object>} The state.
	 */
	function authReady() {
		const api = contract();

		if ( ! api || ! api.ready ) {
			return Promise.resolve( { ...NOBODY } );
		}

		return Promise.resolve( api.ready ).catch( () => ( { ...NOBODY } ) );
	}

	/**
	 * A bearer for the loyalty audience, or '' when there is none.
	 *
	 * **'' rather than null**, because that is what `LoyaltyAPI`'s `accessToken` provider contract
	 * takes and what the library splits on by route: a self-scoped read raises a re-auth error
	 * before the wire, while `/api/tiers` goes out with no `Authorization` header and succeeds. A
	 * `null` would be stringified into the header by some transports.
	 *
	 * **A settled "nobody" is answered without asking the SDK, and that is a real saving rather
	 * than a micro-optimisation.** The library's client calls this provider before EVERY request,
	 * and on the hosted installs `cacheLocation` is `memory` — so each miss is a hidden
	 * `/authorize` iframe. An anonymous visitor, which is most of the site's traffic on every page,
	 * would otherwise pay one per read on top of the one `resolve()` already costs.
	 *
	 * `null` — the SDK has not answered — is NOT short-circuited: it is not "signed out", and a
	 * read issued in that window has to be allowed to obtain a token.
	 *
	 * **The one state this is wrong for, named because it is a fault rather than a surprise:** the
	 * SDK obtains a token and then fails to hand back readable ID-token claims. `runtime.js` settles
	 * that as "nobody" and warns, so the fan resolves as signed out while a usable bearer exists,
	 * and this refuses to send it. That is the documented degradation for the split state — the
	 * header works, the loyalty surfaces do not — and sending a bearer for a fan the site cannot
	 * identify is the worse of the two answers.
	 *
	 * @return {Promise<string>} The token, or ''.
	 */
	function accessToken() {
		const api = contract();

		if ( ! api || typeof api.getAccessToken !== 'function' ) {
			return Promise.resolve( '' );
		}

		const state = typeof api.state === 'function' ? api.state() : null;

		if ( state && ! state.signedIn ) {
			return Promise.resolve( '' );
		}

		return Promise.resolve( api.getAccessToken() )
			.then( token => ( typeof token === 'string' ? token : '' ) )
			.catch( () => '' );
	}

	/**
	 * The raw ID token, or ''.
	 *
	 * **This is what goes to THIS SITE's own endpoints, and `accessToken()` is what goes to the
	 * loyalty API.** The distinction is the whole of [D-068](../../../../docs/decisions.md): an ID
	 * token's `aud` is our own Auth0 client id, so it is addressed to us, while an access token is
	 * addressed to the 3Halves loyalty API — a real third party that receives every fan's token in
	 * the normal course of business. Presenting the latter to WordPress is audience confusion.
	 *
	 * Its one caller is the loyalty consent provider. `''` for the same reason `accessToken()`
	 * answers `''`: the provider refuses before the wire rather than sending an unauthenticated
	 * POST.
	 *
	 * **A settled signed-out state is answered without asking**, exactly as `accessToken()` does
	 * and for the same reason — see there.
	 *
	 * @return {Promise<string>} The raw JWT, or ''.
	 */
	function idToken() {
		const api = contract();

		if ( ! api || typeof api.getIdToken !== 'function' ) {
			return Promise.resolve( '' );
		}

		const state = typeof api.state === 'function' ? api.state() : null;

		if ( state && ! state.signedIn ) {
			return Promise.resolve( '' );
		}

		return Promise.resolve( api.getIdToken() )
			.then( token => ( typeof token === 'string' ? token : '' ) )
			.catch( () => '' );
	}

	/**
	 * The raw ID-token claims the current state was derived from, or null.
	 *
	 * @return {Object|null} The claims.
	 */
	function authClaims() {
		const api = contract();

		return api && typeof api.claims === 'function' ? api.claims() : null;
	}

	/**
	 * Start an interactive login.
	 *
	 * @param {string} [returnTo] Where to land afterwards. Validated against the site's origin.
	 * @return {Promise<void>}
	 */
	function login( returnTo ) {
		const api = contract();

		if ( ! api || typeof api.login !== 'function' ) {
			return Promise.resolve();
		}

		return Promise.resolve( api.login( returnTo ) ).catch( () => undefined );
	}

	return {
		authState,
		isSignedIn,
		subscribeAuth,
		whenSignedIn,
		onIdentityChange,
		authReady,
		accessToken,
		idToken,
		authClaims,
		login,
	};
}

const live = createUrcAuthReader( () =>
	typeof window === 'undefined' ? undefined : window.urcAuth
);

export const authState = live.authState;
export const isSignedIn = live.isSignedIn;
export const subscribeAuth = live.subscribeAuth;
export const whenSignedIn = live.whenSignedIn;
export const onIdentityChange = live.onIdentityChange;
export const authReady = live.authReady;
export const accessToken = live.accessToken;
export const idToken = live.idToken;
export const authClaims = live.authClaims;
export const login = live.login;
